import {chain, omit, sortBy} from 'lodash';
import {Component, ReactNode} from 'react';

import {HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {randomString} from '../../../../libs/shared/helpers/src/utilities/stringHelpers';
import {buildCancelTokenSource, CancelError, CancelToken} from '../../../helpers/asyncCancelHelpers';
import {uploadAttachmentAsync} from '../../../helpers/attachmentHelpers';
import {getFileContentType} from '../../../helpers/fileHelpers';
import {
  ApiServerAttachment,
  AttachmentModel,
  AttachmentStatusesEnum,
  DraftAttachmentModel,
  parseVisualContentType,
  ServerAttachmentModel,
  UploadedAttachmentModel,
} from '../../../models/api/attachmentModel';
import {CompanyModel} from '../../../models/api/companyModel';
import {fetchApiAsync} from '../../../services/http/http';
import {DraggedMessageItemModel} from './attachmentsDragAndDrop';
import {AttachmentFileUploader, AttachmentFileUploaderOptions, UploadingAttachment} from './attachmentsTypes';

/*
 * Props.
 */

export interface AttachmentsTransferProps {
  /** Uploaded attachments and draft attachments still being uploaded. */
  attachments: ReadonlyArray<AttachmentModel>;
  dropAttachment: HandlerOf<UploadedAttachmentModel>;
  onFileUpload: AttachmentFileUploader;
  onDropMessage: HandlerOf<DraggedMessageItemModel>;
  onCancelAttachmentUploadClick: HandlerOf<string>;
}

interface AttachmentsUploaderProps {
  company: CompanyModel;
  /** Existing attachments. */
  attachments: ReadonlyArray<AttachmentModel>;
  /** Attachments from a forwarded EML file message. */
  emlAttachments?: ReadonlyArray<AttachmentModel>;
  onAttachmentUploaded: HandlerOf<UploadedAttachmentModel>;
  children: (props: AttachmentsTransferProps) => ReactNode;
}

/*
 * Component.
 */

interface AttachmentsUploaderState {
  attachments: ReadonlyArray<UploadingAttachment>;
}

export class AttachmentsUploader extends Component<AttachmentsUploaderProps, AttachmentsUploaderState> {
  constructor(props: AttachmentsUploaderProps) {
    super(props);
    this.state = {attachments: []};
  }

  /*
   * Event handlers.
   */

  private readonly uploadFile: AttachmentFileUploader = (
    file,
    {giphyId, isInline = false}: AttachmentFileUploaderOptions = {},
  ) => {
    // TODO: Perform validation on the file type and size.

    const contentType = getFileContentType(file);

    return this.createOrUploadAttachment(
      contentType,
      file.name,
      file.size,
      giphyId,
      isInline,
      async (cancelToken: CancelToken, attachment: DraftAttachmentModel) =>
        uploadAttachmentAsync(
          this.props.company.url,
          file,
          (e) => this.onAttachmentProgressChanged(e, attachment.cid),
          cancelToken,
        ),
    );
  };

  private readonly onDropMessage = (message: DraggedMessageItemModel) => {
    const contentType = 'message/rfc822';
    const size = 0;
    const giphyId = undefined;
    const isInline = false;

    this.createOrUploadAttachment(
      contentType,
      message.emlFileName,
      size,
      giphyId,
      isInline,
      async (cancelToken: CancelToken, attachment: DraftAttachmentModel) =>
        fetchApiAsync(
          ApiServerAttachment,
          `${this.props.company.url}/messages/${message.messageUid}/as-attachment`,
          {cancelToken},
        ),
    );
  };

  private readonly createOrUploadAttachment = (
    contentType: string,
    name: string,
    size: number,
    giphyId: string | undefined,
    isInline: boolean,
    performFetchOrPost: (
      cancelToken: CancelToken,
      attachment: DraftAttachmentModel,
    ) => Promise<ServerAttachmentModel>,
  ) => {
    // Build the new attachment object.
    const attachment: DraftAttachmentModel = {
      status: AttachmentStatusesEnum.DRAFT,
      linkId: undefined,
      uid: randomString(),
      name,
      size,
      visualContentType: parseVisualContentType(contentType),
      contentType,
      cid: randomString(),
      giphyId,
      isInline,
      hasFailed: false,
      progress: 0,
      attachedAt: Date.now(),
      hasThumbnail: false,
    };

    // Keep track of the file and attachment.
    const cancelTokenSource = buildCancelTokenSource();
    const uploadedAttachment = this.createOrUploadAttachmentOnServerAsync(
      attachment,
      performFetchOrPost,
      cancelTokenSource.token,
    );
    const uploadingAttachment: UploadingAttachment = {
      attachment,
      uploadedAttachment,
      cancel: cancelTokenSource.cancel,
    };
    this.setState(({attachments}) => ({attachments: [...attachments, uploadingAttachment]}));

    const {onAttachmentUploaded} = this.props;
    uploadedAttachment.then(onAttachmentUploaded);

    return uploadingAttachment;
  };

  private async createOrUploadAttachmentOnServerAsync(
    attachment: DraftAttachmentModel,
    performFetchOrPost: (
      cancelToken: CancelToken,
      attachment: DraftAttachmentModel,
    ) => Promise<ServerAttachmentModel>,
    cancelToken: CancelToken,
  ): Promise<UploadedAttachmentModel> {
    try {
      // Perform file upload or message-based fetch.
      const serverAttachment = await performFetchOrPost(cancelToken, attachment);

      // When done, remove it from the uploading list.
      this.removeAttachment(attachment.cid);

      // Return the new attachment.
      return {
        ...omit(attachment, 'hasFailed', 'progress'),
        ...serverAttachment,
        status: AttachmentStatusesEnum.UPLOADED,
      };
    } catch (error) {
      if (error instanceof CancelError) {
        this.removeAttachment(attachment.cid);
      } else {
        this.updateAttachment(attachment.cid, {hasFailed: true});
      }

      throw error;
    }
  }

  private readonly dropAttachment = (attachment: UploadedAttachmentModel) => {
    const {attachments, onAttachmentUploaded} = this.props;

    // If we already have this attachment, nothing to do.
    if (attachments.some((a) => a.cid === attachment.cid)) {
      return;
    }

    // If this is an attachment from the EML viewer
    if (attachment.url.startsWith('data:')) {
      this.uploadEmlAttachment(attachment);
      return;
    }

    // Build the new attachment object.
    const newAttachment: UploadedAttachmentModel = {
      ...attachment,
      cid: randomString(),
      isInline: false,
    };

    onAttachmentUploaded(newAttachment);
  };

  private uploadEmlAttachment = (attachment: UploadedAttachmentModel) => {
    const data = tryParseDataUrl(attachment.url);
    if (!data) {
      return;
    }

    this.uploadFile(new File([data], attachment.name, {type: attachment.contentType}));
  };

  private onAttachmentProgressChanged(progress: ProgressEvent, cid: string) {
    const progressPercent = Math.round((100 * progress.loaded) / progress.total);
    this.updateAttachment(cid, {progress: progressPercent});
  }

  private readonly cancelAttachmentUpload = (cid: string) => {
    // Find the uploading attachment, if any.
    const {attachments} = this.state;
    const uploadingAttachment = attachments.find((a) => a.attachment.cid === cid);
    if (!uploadingAttachment) {
      return;
    }

    // Cancel it and remove it from the list.
    uploadingAttachment.cancel();
    this.removeAttachment(cid);
  };

  /*
   * Lifecycle.
   */

  componentDidMount() {
    if (!this.props.emlAttachments) {
      return;
    }

    this.props.emlAttachments.forEach((attachment) => {
      if (attachment.status !== AttachmentStatusesEnum.UPLOADED) {
        return;
      }

      this.uploadEmlAttachment(attachment);
    });
  }

  componentWillUnmount() {
    // Cancel all uploads when unmounting.
    this.state.attachments.forEach((a) => a.cancel());
  }

  /*
   * Attachments manipulation.
   */

  private updateAttachment(cid: string, update: Partial<DraftAttachmentModel>) {
    this.setState(({attachments}) => {
      // Find the existing attachment, if any.
      const uploadingAttachment = attachments.find((a) => a.attachment.cid === cid);
      if (!uploadingAttachment) {
        return null;
      }

      // Update it with the provided data.
      const newAttachment: DraftAttachmentModel = {
        ...uploadingAttachment.attachment,
        ...update,
      };

      const newUploadingAttachment: UploadingAttachment = {
        ...uploadingAttachment,
        attachment: newAttachment,
      };

      // Update the collection.
      const newAttachments = chain(attachments)
        .map((a) => {
          if (a.attachment.cid === cid) {
            return newUploadingAttachment;
          }

          return a;
        })
        .value();

      return {attachments: newAttachments};
    });
  }

  private removeAttachment(cid: string) {
    this.setState(({attachments}) => ({attachments: attachments.filter((a) => a.attachment.cid !== cid)}));
  }

  /*
   * Render.
   */

  render() {
    const draftAttachments = this.state.attachments.map((a) => a.attachment);
    const attachments = sortBy([...this.props.attachments, ...draftAttachments], (a) => a.attachedAt ?? 0);
    const attachmentsProps: AttachmentsTransferProps = {
      attachments,
      dropAttachment: this.dropAttachment,
      onFileUpload: this.uploadFile,
      onDropMessage: this.onDropMessage,
      onCancelAttachmentUploadClick: this.cancelAttachmentUpload,
    };
    return this.props.children(attachmentsProps);
  }
}

const dataUrlRegexp = /^data:.*;base64,(.*)$/;

function tryParseDataUrl(url: string): Uint8Array | null {
  const match = url.match(dataUrlRegexp);
  return match && decodeBase64ToUint8Array(match[1]);
}

// Since the base64 can represent binary data, we don't want to pass it as a string to the File constructor.
function decodeBase64ToUint8Array(base64: string) {
  return Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
}
